/**
 * Agent API Client
 * 
 * Client for interacting with the Context-Aware AI Manager API.
 */

import { apiClient } from './api-client'
import { buildApiUrl } from './api-url'
import { getSession } from 'next-auth/react'

// ============================================================================
// Types
// ============================================================================

export interface StartConversationRequest {
  context_type?: 'incident' | 'general' | 'policy'
  context_id?: string
  initial_message?: string
  context_metadata?: Record<string, unknown>
  auto_execute_safe?: boolean
  skip_ai_defense?: boolean
}

export interface StartConversationResponse {
  conversation_id: string
  title: string
  context_type: string
  agent_response?: AgentResponse
}

export interface SendMessageRequest {
  message: string
  auto_execute_safe?: boolean
  skip_ai_defense?: boolean
}

export interface SendMessageResponse {
  message_id: string
  agent_response: AgentResponse
}

export interface ProvideParamsRequest {
  params: Record<string, string | number | boolean>
  resume_execution?: boolean
}

export interface ClarifyRequest {
  system?: string  // System name (e.g., "CISCO_IOS", "MERAKI")
  selection?: string  // Integration ID (optional)
}

export interface IntegrationSource {
  integration_id: string
  system_type: string
  resource_name?: string
  icon_url?: string
}

export interface WidgetConfig {
  xAxisKey?: string
  yAxisKeys?: string[]
  series?: Array<Record<string, unknown>>
}

export interface WidgetPayload {
  should_render: boolean
  type?: 'LineChart' | 'BarChart' | 'DataTable' | 'InfoCard' | 'CodeBlock'
  title?: string
  description?: string
  data?: unknown
  config?: WidgetConfig
}

export interface AgentResponse {
  type: 'completion' | 'clarification_required' | 'approval_required' | 'error'
  content?: string
  ui_components?: UIComponent[]  // Legacy - deprecated
  widget?: WidgetPayload  // New: Mission Control widget payload
  source?: IntegrationSource  // New: Integration attribution for badges
  suggestions?: string[]  // New: Follow-up action suggestions
  missing_params?: Record<string, ParamSchema>
  clarification_message?: string
  pending_action?: Record<string, unknown>
  approval_message?: string
  error_message?: string
  tool_calls_executed?: ToolCall[]
  follow_up_suggestions?: string[]
  thought_process?: string
}

export interface UIComponent {
  type: 'LineChart' | 'BarChart' | 'DataTable' | 'InfoCard' | 'CodeBlock'
  title?: string
  data: Record<string, unknown>
}

export interface ParamSchema {
  name: string
  description: string
  type: string
  required: boolean
  enum?: string[]
  default?: string | number | boolean
  placeholder?: string
}

export interface ToolCall {
  id: string
  name: string
  original_name?: string
  args: Record<string, unknown>
  result?: unknown
  error?: string
  execution_time_ms?: number
  safety?: string
}

export interface ConversationSummary {
  id: string
  title: string
  context_type: string
  context_id?: string
  created_at: string
  last_message_at: string
  message_count?: number
}

// ============================================================================
// API Functions
// ============================================================================

export const agentApi = {
  /**
   * Start a new conversation
   */
  async startConversation(request: StartConversationRequest): Promise<StartConversationResponse> {
    return apiClient.post<StartConversationResponse>('/api/ai/chat/start', request)
  },

  /**
   * Send a message in an existing conversation
   */
  async sendMessage(conversationId: string, request: SendMessageRequest): Promise<SendMessageResponse> {
    return apiClient.post<SendMessageResponse>(`/api/ai/chat/${conversationId}/message`, request)
  },

  /**
   * Provide missing parameters
   */
  async provideParams(conversationId: string, request: ProvideParamsRequest): Promise<{ agent_response: AgentResponse }> {
    return apiClient.post(`/api/ai/chat/${conversationId}/provide-params`, request)
  },

  /**
   * Provide clarification about which system/integration to use
   */
  async clarify(conversationId: string, request: ClarifyRequest): Promise<{ agent_response: AgentResponse }> {
    return apiClient.post(`/api/ai/chat/${conversationId}/clarify`, request)
  },

  /**
   * List user's conversations
   */
  async listConversations(contextType?: string, limit = 50): Promise<{ conversations: ConversationSummary[] }> {
    const params = new URLSearchParams()
    if (contextType) params.append('context_type', contextType)
    params.append('limit', String(limit))
    
    return apiClient.get(`/api/ai/chat/conversations?${params.toString()}`)
  },

  /**
   * Get a specific conversation with full message history
   */
  async getConversation(conversationId: string): Promise<{ conversation: ConversationContext }> {
    return apiClient.get(`/api/ai/chat/${conversationId}`)
  },

  /**
   * Delete a conversation and all its messages
   */
  async deleteConversation(conversationId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/api/ai/chat/${conversationId}`)
  },

  /**
   * Stream a message via SSE (Server-Sent Events).
   * Calls onEvent for each event emitted by the backend as the agent processes.
   * 
   * Event types:
   *  - thinking: { type: 'thinking', content: string }
   *  - token:    { type: 'token', content: string }  ← progressive text tokens
   *  - tool_call: { type: 'tool_call', content: string, tool: ToolCall }
   *  - done:     { type: 'done', content: '', agent_response: AgentResponse }
   *  - error:    { type: 'error', content: string }
   * 
   * Returns a cleanup function to abort the stream.
   */
  streamMessage(
    conversationId: string,
    message: string,
    options: { skipAiDefense?: boolean; autoExecuteSafe?: boolean },
    onEvent: (event: { type: string; content: string; agent_response?: AgentResponse; tool?: ToolCall }) => void
  ): () => void {
    const params = new URLSearchParams({
      message,
      skip_ai_defense: String(options.skipAiDefense ?? false),
      auto_execute_safe: String(options.autoExecuteSafe ?? false),
    })
    
    const controller = new AbortController()
    
    ;(async () => {
      try {
        const session = await getSession()
        const headers: Record<string, string> = {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        }
        
        if (session?.accessToken) {
          headers['Authorization'] = `Bearer ${session.accessToken}`
        } else {
          // Fallback to localStorage for edge cases during dev
          try {
            if (typeof window !== 'undefined') {
              const keycloakToken = localStorage.getItem('keycloak_token')
              if (keycloakToken) {
                headers['Authorization'] = `Bearer ${keycloakToken}`
              }
            }
          } catch {
            // ignore
          }
        }

        const fullUrl = buildApiUrl(`/api/ai/chat/${conversationId}/stream?${params.toString()}`)
        const response = await fetch(
          fullUrl,
          {
            method: 'GET',
            headers,
            signal: controller.signal,
          }
        )
        
        if (!response.ok || !response.body) {
          onEvent({ type: 'error', content: `Stream failed: ${response.status}` })
          return
        }
        
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''  // Keep incomplete last line
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const eventData = JSON.parse(line.slice(6))
                onEvent(eventData)
              } catch {
                // ignore malformed events
              }
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          onEvent({ type: 'error', content: `Stream error: ${err.message}` })
        }
      }
    })()
    
    // Return cleanup function
    return () => controller.abort()
  },
}

export interface ConversationContext {
  conversation_id: string
  type: string
  context_id?: string
  title: string
  user_id: string
  organization_id: string
  context_metadata: Record<string, unknown>
  created_at: string
  history: ConversationHistoryMessage[]
  incident?: Record<string, unknown>
  policy?: Record<string, unknown>
}

export interface ConversationHistoryMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  tool_calls?: ToolCall[]
  ui_components?: UIComponent[]
  created_at: string
}
